import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { Avatar, Box, colors, List } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import axios from "axios";
import SearchBar from "../../components/Phase2/Searchbar";
import PostCard from "../../components/lists/PostCard";
import LoadingModal from "../../components/LoadingModal";
import MebukuIdUpgradeBanner from "../../components/MebukuIdUpgradeBanner";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import GroupIconImage from "../../components/GreenCity/GroupIconImage";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import appServices from "../../services/app-services/appServices";
import { convertUserImage } from "../../util/utils";
import dayjs from "../../util/dayjs";

const SearchScreen = () => {
  const navigate = useNavigate()
  const location = useLocation();
  const [posts, setPosts] = useState<any>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [reSearch, setReSearch] = useState<boolean>(false);
  const [isPostSearch, setIsPostSearch] = useState<boolean>(true);
  const [activeUserTab, setActiveUserTab] = useState<boolean>(false);
  const groupTabRef = useRef<HTMLDivElement | null>(null)
  const userTabRef = useRef<HTMLDivElement | null>(null)
  const [isSearched, setIsSearched] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string[]>([])
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)

  const handleSearch = async (term: string, token: any) => {
    const cleanedTerm = term.replace(/^[\s\u3000]+/, '');
    const postSearch = !(cleanedTerm.startsWith("@") || cleanedTerm.startsWith("＠"))
    setIsPostSearch(postSearch)
    setActiveUserTab(false)

    try {
      setLoading(true);
      const response = await appServices.globalSearch(cleanedTerm, postSearch, token);
      const data = response.result
      if (postSearch) {
        setPosts(data.posts);
        setGroups([])
        setUsers([])
      } else {
        setPosts([]);
        setGroups(data.member_groups)
        const userList = data.users.concat(data.navigators)
          .sort((a: any, b: any) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf())
        setUsers(userList)
      }
    } catch (error: any) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
        setErrorMessage(error?.response?.data?.message)
        setShowErrorModal(true)
        setGroups([])
        setUsers([])
        setPosts([]);
      }
    } finally {
      setIsSearched(true)
      setReSearch(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const groupTab = groupTabRef.current
    const userTab = userTabRef.current

    if (!groupTab || !userTab) return

    if (activeUserTab) {
      userTab.style.borderBottom = `4px solid ${colors.green[400]}`
      groupTab.style.borderBottom = `none`
    } else {
      groupTab.style.borderBottom = `4px solid ${colors.green[400]}`
      userTab.style.borderBottom = `none`
    }

  }, [activeUserTab, isPostSearch])

  const navigateGroupTop = (group: any) => {
    navigate("/groupinforeview", {state: {groupId: group.id}});
  };

  const handleSelectUser= (user: any) => {
    navigate("/viewGroupPost", {
      state: {
        post_type: "user",
        user: user
      },
    });
  }

  const closeErrorModal = () => {
    setShowErrorModal(false)
    setIsSearched(false)
    setErrorMessage([])
  }

  return (
    <div style={{ paddingTop: 100 }}>
      <LoadingModal visible={loading} handleClose={() => {}} />
      <Box sx={styles.goBack}>
        <GCBackButton onClick={() => navigate(-1)} />
      </Box>

      <div style={styles.searchContainer}>
        <div>
          <SearchBar focus reSearch={reSearch} onSearch={handleSearch} initTerm={location?.state?.termProps ?? ""}/>
        </div>
        <div style={styles.searchNotice}>
          {T("search_how_to_search_user_or_group")}
        </div>
      </div>

      {isPostSearch ? (
        isSearched && posts.length === 0 ? (
          <div style={styles.notResults}>
            {T("search_not_found")}
          </div>
        ) : (
          <List>
            {posts?.map((item: any, index: number) => (
              <PostCard
                post={item}
                key={index}
              />
            ))}
          </List>
        )
      ) : (
        <>
          <div style={styles.tabContainer}>
            <div style={styles.tab} onClick={() => setActiveUserTab(false)} ref={groupTabRef}>
              {T("group")}
            </div>
            <div style={styles.tab} onClick={() => setActiveUserTab(true)} ref={userTabRef}>
              {T("user")}
            </div>
          </div>

          <div style={styles.listContainer}>
            {activeUserTab ? (
              isSearched && users.length === 0 ? (
                <div style={styles.notResults}>
                  {T("search_not_found")}
                </div>
              ) : (
                users.map((userData: any) => (
                  <div key={`${userData.class_name}_${userData?.id}`} style={styles.data} onClick={() => handleSelectUser(userData)}>
                    <Avatar sx={styles.userImage} src={convertUserImage(userData.image)} />
                    <div style={styles.name}>
                      {userData.nickname || ""}
                      <ArrowForwardIosIcon style={styles.arrowIcon} />
                    </div>
                  </div>
                ))
              )
            ) : (
              isSearched && groups.length === 0 ? (
                <div style={styles.notResults}>
                  {T("search_not_found")}
                </div>
              ) : (
                groups.map((group: any) => (
                  <div key={`group_${group?.id}`} style={styles.data} onClick={() => navigateGroupTop(group)}>
                    <GroupIconImage groupId={group?.id} alt="" style={styles.groupIcon} />
                    <div style={styles.name}>
                      {group?.name || ""}
                      <ArrowForwardIosIcon style={styles.arrowIcon} />
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </>
      )}
      <MebukuIdUpgradeBanner />
      <ErrorMessageModal
        open={showErrorModal}
        messages={errorMessage}
        onClose={() => closeErrorModal()}
      />
    </div>
  );
};

export default SearchScreen;

const styles = {
  goBack: {
    margin: "0 0 14px 10px"
  },

  searchContainer: {
    padding: "0 20px 20px",
    borderBottom: `2px solid ${colors.grey[400]}`
  },

  searchNotice: {
    marginTop: 15,
    color: '#5D5D5D',
    lineHeight: "22px",
    fontSize: 14
  },

  tabContainer: {
    marginTop: 15,
    padding: "0 20px",
    borderBottom: `2px solid ${colors.grey[400]}`,
    display: 'flex',
    gap: 20
  },

  tab: {
    paddingBottom: 8,
    letterSpacing: 1.5
  },

  listContainer: {
    padding: "20px 20px 0",
  },

  data: {
    padding: "10px 10px 10px",
    borderBottom: `2px solid ${colors.grey[400]}`,
    display: 'flex',
  },

  name: {
    width: '100%',
    marginLeft: 10,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  arrowIcon: {
    marginRight: 2,
    marginLeft: 4,
    fontSize: 16,
  },

  groupIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
  },

  userImage: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    objectFit: "cover",
  },

  notResults: {
    width: "100%",
    textAlign: "center" as "center",
    marginTop: '50px',
    fontSize: 16
  },
}
